import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import type { HierarchyMultiSelectProps } from './HierarchyMultiSelect.types';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/cn';

type ParentKey = string | undefined;

const DEFAULT_PAGE_SIZE = 50;

interface HierarchyMultiSelectOptionItemProps {
  itemId: string;
  itemLabel: string;
  queryKey: string;
  isSelected: boolean;
  checkHasChildren: () => Promise<boolean>;
  onToggle: () => void;
  onGoToChildren: () => void;
}

function HierarchyMultiSelectOptionItem({
  itemId,
  itemLabel,
  queryKey,
  isSelected,
  checkHasChildren,
  onToggle,
  onGoToChildren,
}: HierarchyMultiSelectOptionItemProps) {
  const { data: hasChildren = false } = useQuery({
    queryKey: [queryKey, 'children-check', itemId],
    queryFn: checkHasChildren,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <DropdownMenuCheckboxItem
      checked={isSelected}
      onSelect={(e) => {
        e.preventDefault();
        onToggle();
      }}
      className={cn(
        'h-12 cursor-pointer py-2.5 pr-3 text-sm text-primary',
        'flex items-center justify-between gap-2',
      )}
    >
      <span className="flex-1 truncate text-left">{itemLabel}</span>
      {hasChildren && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onGoToChildren();
          }}
          className="ml-2 rounded p-1 text-primary opacity-50 hover:bg-prominent hover:opacity-100"
          aria-label={`Ver subniveles de ${itemLabel}`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </DropdownMenuCheckboxItem>
  );
}

interface SelectedChipProps<T extends { id: string }> {
  id: string;
  queryKey: string;
  fetchItemById: (id: string) => Promise<T>;
  getLabel: (item: T) => string;
  getParent: (item: T) => string | null | undefined;
  /**
   * Label already known synchronously at selection time (e.g. the item was visible in the
   * currently rendered dropdown page when the user checked it). Shown immediately so the chip
   * never flashes the raw id while the async breadcrumb query below resolves.
   */
  initialLabel?: string;
  onRemove: () => void;
}

function SelectedChip<T extends { id: string }>({
  id,
  queryKey,
  fetchItemById,
  getLabel,
  getParent,
  initialLabel,
  onRemove,
}: SelectedChipProps<T>) {
  const { data: path } = useQuery<string>({
    queryKey: [queryKey, 'path', id],
    queryFn: async () => {
      const names: string[] = [];
      const visited = new Set<string>();
      let currentId: string | undefined = id;
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const item = await fetchItemById(currentId);
        names.unshift(getLabel(item));
        currentId = getParent(item) ?? undefined;
      }
      return names.join(' / ');
    },
    staleTime: 5 * 60 * 1000,
  });

  const displayLabel = path ?? initialLabel ?? id;

  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-outline bg-prominent px-2.5 py-1 text-xs text-primary">
      {displayLabel}
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full p-0.5 text-primary opacity-70 hover:bg-select-background hover:opacity-100"
        aria-label={`Quitar ${displayLabel}`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

export function HierarchyMultiSelect<T extends { id: string }>({
  value,
  onValueChange,
  fetchItems,
  fetchItemById,
  getLabel,
  getParent,
  queryKey,
  placeholder = 'Seleccionar',
  label,
  helperText,
  error,
  disabled = false,
  className,
  cascading = false,
}: HierarchyMultiSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [currentParentId, setCurrentParentId] = useState<ParentKey>(undefined);
  const [parentHistory, setParentHistory] = useState<ParentKey[]>([]);
  const [parentNameHistory, setParentNameHistory] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState<number>();
  const [knownLabels, setKnownLabels] = useState<Record<string, string>>({});

  const selectedIds = value ?? [];

  const {
    data: itemsData,
    refetch: refetchItems,
    isFetching: isFetchingItems,
  } = useQuery({
    queryKey: [queryKey, 'items', currentParentId ?? 'root', pageSize],
    queryFn: () => fetchItems({ parentId: currentParentId, page: 1, pageSize }),
  });

  const currentOptions = useMemo(() => itemsData?.items ?? [], [itemsData]);
  const hasMore = itemsData?.hasMore ?? false;
  const handleLoadMore = () => setPageSize((prev) => prev + DEFAULT_PAGE_SIZE);

  const displayValue = selectedIds.length > 0 ? `${selectedIds.length} seleccionado(s)` : '';

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      setContentWidth(triggerRef.current?.offsetWidth);
      void refetchItems();
    }
  };

  const handleGoToChildren = (itemId: string, itemName: string) => {
    setParentHistory((prev) => [...prev, currentParentId]);
    setParentNameHistory((prev) => [...prev, itemName]);
    setCurrentParentId(itemId);
    setPageSize(DEFAULT_PAGE_SIZE);
  };

  const handleBack = () => {
    setParentHistory((prev) => {
      if (prev.length === 0) {
        setCurrentParentId(undefined);
        return prev;
      }
      const newHistory = [...prev];
      const previousParent = newHistory.pop();
      setCurrentParentId(previousParent);
      setParentNameHistory((prevNames) => {
        const updated = [...prevNames];
        updated.pop();
        return updated;
      });
      return newHistory;
    });
    setPageSize(DEFAULT_PAGE_SIZE);
  };

  const fetchAllDescendants = async (parentId: string): Promise<string[]> => {
    const { items } = await fetchItems({ parentId, page: 1, pageSize: 100 });
    const descendants: string[] = [];
    for (const item of items) {
      descendants.push(item.id);
      descendants.push(...(await fetchAllDescendants(item.id)));
    }
    return descendants;
  };

  const toggleItem = async (id: string, label?: string) => {
    if (cascading) {
      if (selectedIds.includes(id)) {
        const descendants = await fetchAllDescendants(id);
        const idsToRemove = new Set([id, ...descendants]);
        onValueChange(selectedIds.filter((selected) => !idsToRemove.has(selected)));
      } else {
        const descendants = await fetchAllDescendants(id);
        const idsToAdd = new Set([id, ...descendants]);
        // Remember labels we already know from the currently rendered dropdown page so the
        // newly added descendant chips can show them immediately instead of raw ids while the
        // async breadcrumb resolves.
        setKnownLabels((prev) => {
          const next = { ...prev };
          if (label) next[id] = label;
          for (const item of currentOptions) {
            if (idsToAdd.has(item.id)) {
              next[item.id] = getLabel(item);
            }
          }
          return next;
        });
        onValueChange(Array.from(new Set([...selectedIds, ...idsToAdd])));
      }
      return;
    }

    if (selectedIds.includes(id)) {
      onValueChange(selectedIds.filter((selected) => selected !== id));
    } else {
      // Remember the label we already know from the currently rendered dropdown page so the
      // chip can show it immediately instead of the raw id while the async breadcrumb resolves.
      if (label) {
        setKnownLabels((prev) => ({ ...prev, [id]: label }));
      }
      onValueChange([...selectedIds, id]);
    }
  };

  const removeItem = (id: string) => {
    onValueChange(selectedIds.filter((selected) => selected !== id));
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <div ref={triggerRef} className="w-full">
            <Input
              type="text"
              value={displayValue}
              readOnly
              placeholder={placeholder}
              label={label}
              helperText={helperText}
              error={error}
              disabled={disabled}
              className={cn('w-full cursor-pointer', className)}
              iconEnd={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  className={cn(
                    'opacity-50 transition-transform duration-200',
                    open ? 'rotate-180' : 'rotate-0',
                  )}
                >
                  <path d="M7 9.5L12 14.5L17 9.5H7Z" fill="#868686" />
                </svg>
              }
            />
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="start"
          sideOffset={4}
          className="z-50 overflow-y-auto rounded-lg border border-outline bg-select-background p-0 shadow-lg"
          style={{ width: contentWidth ? `${contentWidth}px` : 'auto' }}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          {parentHistory.length > 0 && (
            <div className="flex items-center gap-2 border-b border-outline px-3 py-2 text-sm text-primary">
              <button
                type="button"
                onClick={handleBack}
                className="flex shrink-0 items-center gap-1 rounded p-1 hover:bg-prominent"
              >
                <ChevronLeft className="h-4 w-4" />
                Atrás
              </button>
              <span className="truncate text-xs opacity-60">{parentNameHistory.join(' / ')}</span>
            </div>
          )}

          {currentOptions.length === 0 && (
            <div className="px-3 py-2 text-sm text-primary opacity-50">Sin opciones</div>
          )}

          {currentOptions.map((item) => {
            const itemLabel = getLabel(item);
            return (
              <HierarchyMultiSelectOptionItem
                key={item.id}
                itemId={item.id}
                itemLabel={itemLabel}
                queryKey={queryKey}
                isSelected={selectedIds.includes(item.id)}
                checkHasChildren={() =>
                  fetchItems({ parentId: item.id, page: 1, pageSize: 1 }).then(
                    (r) => r.items.length > 0,
                  )
                }
                onToggle={() => toggleItem(item.id, itemLabel)}
                onGoToChildren={() => handleGoToChildren(item.id, itemLabel)}
              />
            );
          })}

          {hasMore && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleLoadMore();
              }}
              disabled={isFetchingItems}
              className="w-full border-t border-outline px-3 py-2 text-center text-sm text-primary opacity-70 hover:bg-prominent hover:opacity-100 disabled:cursor-not-allowed"
            >
              {isFetchingItems ? 'Cargando...' : 'Ver más'}
            </button>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="hierarchy-multiselect-chips">
          {selectedIds.map((id) => (
            <SelectedChip<T>
              key={id}
              id={id}
              queryKey={queryKey}
              fetchItemById={fetchItemById}
              getLabel={getLabel}
              getParent={getParent}
              initialLabel={knownLabels[id]}
              onRemove={() => removeItem(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
